import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging, isSupported } from 'firebase/messaging';

export const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID
};

const firebaseApp = initializeApp(firebaseConfig);
let messaging: Messaging | null = null;
let isMessagingInitialized = false;

export const initializeFirebaseMessaging = async (): Promise<Messaging | null> => {
  try {
    if (isMessagingInitialized && messaging) {
      return messaging;
    }

    const isMessagingSupported = await isSupported();
    if (isMessagingSupported) {
      messaging = getMessaging(firebaseApp);
      isMessagingInitialized = true;
      console.log('Firebase Messaging initialized');
      return messaging;
    } else {
      console.warn('Firebase Messaging not supported');
      return null;
    }
  } catch (error) {
    console.error('Failed to initialize Firebase Messaging:', error);
    return null;
  }
};

export const getOrRegisterServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service worker not supported');
  }

  try {
    const existingRegistration = await navigator.serviceWorker.getRegistration('/');

    if (existingRegistration && existingRegistration.active) {
      return existingRegistration;
    }

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
      updateViaCache: 'none'
    });

    await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.error('Service worker registration failed:', error);
    throw error;
  }
};

export const getFirebaseToken = async (): Promise<string | null> => {
  try {
    const messagingInstance = await initializeFirebaseMessaging();
    if (!messagingInstance) {
      console.error('Firebase Messaging not available');
      return null;
    }

    const registration = await getOrRegisterServiceWorker();
    const currentToken = await getToken(messagingInstance, {
      vapidKey: process.env.REACT_APP_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (currentToken) {
      console.log('FCM Token obtained');
      return currentToken;
    } else {
      console.warn('No registration token available');
      return null;
    }
  } catch (error) {
    console.error('Error retrieving token:', error);
    return null;
  }
};

export const initializeForegroundMessageHandler = async (): Promise<boolean> => {
  try {
    const messagingInstance = await initializeFirebaseMessaging();
    if (!messagingInstance) {
      console.error('Firebase Messaging not initialized');
      return false;
    }

    onMessage(messagingInstance, (payload) => {
      console.log('Foreground message received:', payload);

      if (payload.notification && Notification.permission === 'granted') {
        const { title, body } = payload.notification;

        const notification = new Notification(title || 'New Notification', {
          body: body || 'You have a new message',
          icon: '../public/favicon.ico',
          badge: '../public/favicon.ico',
          tag: 'foreground-notification-' + Date.now(),
          requireInteraction: false,
          data: payload.data,
          silent: false
        });

        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          notification.close();

          if (payload.data?.click_action === 'BROADCAST') {
            window.location.href = '/broadcasts';
          }
        };

        setTimeout(() => notification.close(), 5000);
      }

      if (payload.data?.badge) {
        if (typeof window !== 'undefined' && (window as any).dispatchNotificationCount) {
          (window as any).dispatchNotificationCount(payload.data.badge);
        }
      }
    });

    console.log('Foreground message handler initialized');
    return true;
  } catch (error) {
    console.error('Error initializing foreground message handler:', error);
    return false;
  }
};

export const setupInstantNotifications = async (): Promise<string | null> => {
  try {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      console.warn('Browser does not support required features');
      return null;
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission denied');
        return null;
      }
    }

    if (Notification.permission !== 'granted') {
      console.error('Notification permission not granted');
      return null;
    }

    await initializeForegroundMessageHandler();
    const token = await getFirebaseToken();

    if (token) {
      console.log('Instant notifications setup complete');
    }

    return token;
  } catch (error) {
    console.error('Error setting up notifications:', error);
    return null;
  }
};

export const getMessagingInstance = async (): Promise<Messaging | null> => {
  return await initializeFirebaseMessaging();
};

export { messaging };
